import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Circle } from 'lucide-react';
import TacticalCanvasBase from '../components/tactical/TacticalCanvasBase';
import { BRAND_GREEN, BRAND_GOLD } from '../theme/sucronTheme';


const TacticsViewerPage = () => {
  const { id } = useParams();
  const [play, setPlay] = useState(null);
  const [checkState, setCheckState] = useState({});

  useEffect(() => {
    try {
      const all = JSON.parse(localStorage.getItem('arenastats_tactics_plays') || '[]');
      const found = all.find((p) => p.id === id);
      if (found) {
        setPlay(found);
        // Restore checklist state from localStorage
        const stored = localStorage.getItem(`arenastats_tactics_check_${id}`);
        if (stored) setCheckState(JSON.parse(stored));
      }
    } catch { /* ignore */ }
  }, [id]);

  const toggleCheck = (idx) => {
    setCheckState((prev) => {
      const next = { ...prev, [idx]: !prev[idx] };
      localStorage.setItem(`arenastats_tactics_check_${id}`, JSON.stringify(next));
      return next;
    });
  };

  if (!play) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Jogada nao encontrada.</p>
          <Link to="/tactics/designer" className="text-sm font-semibold" style={{ color: BRAND_GREEN }}>
            Voltar ao Designer
          </Link>
        </div>
      </div>
    );
  }

  const completedCount = Object.values(checkState).filter(Boolean).length;
  const totalChecks = (play.checklist || []).length;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link
            to="/tactics/designer"
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-100"
          >
            <ArrowLeft size={16} />
          </Link>
          <div>
            <h1 className="text-2xl font-extrabold" style={{ color: BRAND_GREEN }}>
              {play.playName || 'Sem nome'}
            </h1>
            <p className="text-gray-500 text-sm">
              {play.sportKey === 'football_american' ? 'Futebol Americano' : 'Futebol'} &middot;{' '}
              {new Date(play.createdAt).toLocaleDateString('pt-BR')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Canvas read-only */}
          <div className="lg:col-span-2">
            <TacticalCanvasBase
              sportKey={play.sportKey || 'football'}
              players={play.players || []}
              paths={[]}
              readOnly
              width={800}
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Checklist */}
            {totalChecks > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Checklist</h3>
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: completedCount === totalChecks ? '#f3f4f6' : '#fef3c7',
                      color: completedCount === totalChecks ? BRAND_GREEN : BRAND_GOLD,
                    }}
                  >
                    {completedCount}/{totalChecks}
                  </span>
                </div>
                <ul className="space-y-2">
                  {play.checklist.map((c, idx) => {
                    const done = !!checkState[idx];
                    return (
                      <li
                        key={idx}
                        onClick={() => toggleCheck(idx)}
                        className="flex items-center gap-2 cursor-pointer py-1 px-2 rounded hover:bg-gray-50 transition-colors duration-200"
                      >
                        {done ? (
                          <CheckCircle size={16} style={{ color: BRAND_GREEN }} />
                        ) : (
                          <Circle size={16} className="text-gray-300" />
                        )}
                        <span className={`text-sm ${done ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                          {c.text}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Players */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Jogadores ({(play.players || []).length})
              </h3>
              <ul className="space-y-1">
                {(play.players || []).map((p) => (
                  <li key={p.id} className="flex items-center justify-between text-sm py-1 px-2 rounded hover:bg-gray-50">
                    <span className="font-medium" style={{ color: BRAND_GREEN }}>{p.label}</span>
                    <span className="text-gray-400 text-xs">
                      ({Math.round(p.x)}, {Math.round(p.y)})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TacticsViewerPage;
